import { useEffect, useMemo, useState } from 'react';
import type { Programme } from '../types';
import { listProgrammes } from '../lib/api';
import { IconRefresh, IconChevronRight } from '../components/icons';
import { SkeletonRows } from '../components/Skeleton';
import { useToast } from '../lib/toast';
import { ProgrammeEditDrawer } from './ProgrammeEditDrawer';
import { relativeDate } from '../lib/format';

interface Props {
  /** Remonté à App.tsx pour piloter le pip "À refresh" en sidebar (cf. P1). */
  onRefreshCountChange?: (n: number) => void;
  /**
   * Remonté à App.tsx pour rafraîchir le count global Programmes en sidebar
   * quand une mutation se fait depuis le kanban (cf. audit B1 : sinon le
   * count restait stale jusqu'au prochain mount de la page Programmes).
   */
  onProgrammesCountChange?: (n: number) => void;
}

type ColumnId = 'to-refresh' | 'in-progress' | 'ready' | 'published-month';

interface ColumnDef {
  id: ColumnId;
  label: string;
  description: string;
  accent: string;
  match: (p: Programme) => boolean;
}

const STALE_DAYS = 60;
const PUBLISHED_RECENT_DAYS = 30;

function daysSince(iso?: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Critères de tri par colonne (cf. brief V2 §10.3 Brick pipeline).
 *
 * - À refresh : statut publi != Archivé et (jamais vérifié OU vérifié > 60j).
 *   C'est le seul état qui mérite un pip d'urgence en sidebar.
 * - En cours : statut Draft (et pas dans À refresh).
 * - Prêts à publier : statut Draft + vérifié récemment + Nom FR+EN complets.
 * - Publiés ce mois : statut Publié + vérifié dans les 30j.
 *
 * Un programme peut tomber dans 1 seule colonne (priorité par ordre dans
 * COLUMNS).
 */
const COLUMNS: ColumnDef[] = [
  {
    id: 'to-refresh',
    label: 'À refresh',
    description: 'Jamais vérifié ou dernière vérif > 60 jours',
    accent: 'border-amber bg-amber-soft/30',
    match: (p) => {
      if (p.publicationStatus === 'Archivé') return false;
      const days = daysSince(p.lastVerifiedDate);
      return days === null || days > STALE_DAYS;
    },
  },
  {
    id: 'in-progress',
    label: 'En cours',
    description: 'Draft ou statut non défini, en cours d\'édition',
    accent: 'border-founders bg-founders-soft/30',
    // Accepte Draft explicite ET statut vide/undefined (draft implicite).
    // Sinon les programmes sans statut deviennent orphans dès qu'on
    // touche leur date, ce qui les fait disparaître du kanban.
    match: (p) => !p.publicationStatus || p.publicationStatus === 'Draft',
  },
  {
    id: 'ready',
    label: 'Prêts à publier',
    description: 'Draft vérifié récemment, parité bilingue OK',
    accent: 'border-funds bg-funds-soft/30',
    match: (p) => {
      const isDraftish = !p.publicationStatus || p.publicationStatus === 'Draft';
      if (!isDraftish) return false;
      if (!p.nameFr || !p.nameEn) return false;
      const days = daysSince(p.lastVerifiedDate);
      return days !== null && days <= STALE_DAYS;
    },
  },
  {
    id: 'published-month',
    label: 'Publiés ce mois',
    description: 'Publié dans les 30 derniers jours',
    accent: 'border-navy bg-cream-tint',
    match: (p) => {
      if (p.publicationStatus !== 'Publié') return false;
      const days = daysSince(p.lastVerifiedDate);
      return days !== null && days <= PUBLISHED_RECENT_DAYS;
    },
  },
];

/**
 * Catégorise un programme dans la PREMIÈRE colonne dont le match passe.
 * Ordre = priorité (À refresh > En cours > Prêts > Publiés).
 * Retourne null si aucune colonne ne match (cas : Publié il y a > 30j, Archivé, etc.).
 */
function categorize(p: Programme): ColumnId | null {
  for (const col of COLUMNS) {
    if (col.match(p)) return col.id;
  }
  return null;
}

export function BrickPipelinePage({ onRefreshCountChange, onProgrammesCountChange }: Props) {
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Programme | null>(null);
  const toast = useToast();

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listProgrammes();
      setProgrammes(res.programmes);
      onProgrammesCountChange?.(res.programmes.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load programmes');
      setProgrammes([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const byColumn = useMemo(() => {
    const groups: Record<ColumnId, Programme[]> = {
      'to-refresh': [],
      'in-progress': [],
      'ready': [],
      'published-month': [],
    };
    for (const p of programmes) {
      const col = categorize(p);
      if (col) groups[col].push(p);
    }
    return groups;
  }, [programmes]);

  // Remonte le count "À refresh" pour le pip sidebar.
  useEffect(() => {
    onRefreshCountChange?.(byColumn['to-refresh'].length);
  }, [byColumn, onRefreshCountChange]);

  function onSaved(updated: Programme) {
    setProgrammes((list) => list.map((p) => (p.id === updated.id ? updated : p)));
    setEditing(updated);
  }

  function onDeleted(id: string) {
    setProgrammes((list) => {
      const next = list.filter((p) => p.id !== id);
      onProgrammesCountChange?.(next.length);
      return next;
    });
    setEditing(null);
    toast.success('Programme supprimé', '');
  }

  const orphanCount = programmes.length - Object.values(byColumn).flat().length;

  return (
    <div className="px-4 md:px-8 py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-display tracking-tightish text-ink">
            Brick pipeline
          </h1>
          <p className="mt-1 text-[13.5px] text-ink-muted max-w-2xl">
            Tracking éditorial des programmes non-dilutifs. Quatre colonnes calculées depuis
            le statut et la date de dernière vérification. Click sur une carte pour ouvrir le
            drawer d&apos;édition.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="h-9 px-3 rounded-md border border-line bg-white text-[13px] text-ink-muted hover:bg-cream-tint transition disabled:opacity-50 flex items-center gap-1.5"
          >
            <IconRefresh className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          <span className="font-medium">Erreur : </span>{error}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {COLUMNS.map((col) => {
          const n = byColumn[col.id].length;
          return (
            <div key={col.id} className="rounded-md border border-line-soft bg-cream p-3 shadow-card">
              <p className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">{col.label}</p>
              <p className="text-[22px] font-display tracking-tightish text-ink mt-1 num">{n}</p>
            </div>
          );
        })}
      </div>

      {/* Kanban columns */}
      {loading && programmes.length === 0 ? (
        <div className="rounded-md border border-line-soft bg-cream p-4 shadow-card">
          <SkeletonRows rows={4} cols={4} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {COLUMNS.map((col) => (
            <Column
              key={col.id}
              def={col}
              programmes={byColumn[col.id]}
              onCardClick={setEditing}
            />
          ))}
        </div>
      )}

      {/* Footer info : programmes orphelins (Publiés > 30j, Archivés, Rejetés) */}
      {orphanCount > 0 && (
        <p className="mt-4 text-[12px] text-ink-faint text-center">
          {orphanCount} programme{orphanCount > 1 ? 's' : ''} hors pipeline (Archivés, Rejetés, ou Publiés il y a {'>'}{PUBLISHED_RECENT_DAYS}j),
          visibles dans la page Programmes non-dilutifs.
        </p>
      )}

      {/* Drawer édition */}
      {editing && (
        <ProgrammeEditDrawer
          programme={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}

function Column({ def, programmes, onCardClick }: {
  def: ColumnDef;
  programmes: Programme[];
  onCardClick: (p: Programme) => void;
}) {
  return (
    <div className={'rounded-md border bg-white shadow-card flex flex-col min-h-[200px] ' + def.accent}>
      <div className="px-3 py-2.5 border-b border-line-soft bg-white/50">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-[13px] font-medium text-ink">{def.label}</h3>
          <span className="num text-[11px] text-ink-muted bg-white/80 px-1.5 py-0.5 rounded-full border border-line-soft">
            {programmes.length}
          </span>
        </div>
        <p className="mt-0.5 text-[10.5px] text-ink-faint">{def.description}</p>
      </div>
      <div className="flex-1 p-2 space-y-1.5 overflow-y-auto max-h-[60vh]">
        {programmes.length === 0 ? (
          <div className="text-center text-[11px] text-ink-faint py-6">Aucun programme ici</div>
        ) : (
          programmes.map((p) => (
            <Card key={p.id} programme={p} onClick={() => onCardClick(p)} />
          ))
        )}
      </div>
    </div>
  );
}

function Card({ programme: p, onClick }: { programme: Programme; onClick: () => void }) {
  const bilingueOk = !!(p.nameFr && p.nameEn);
  const days = daysSince(p.lastVerifiedDate);
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-md border border-line-soft bg-white px-3 py-2 hover:border-founders/40 hover:shadow-card transition group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[12.5px] font-medium text-ink truncate">
              {p.nameFr || p.nameEn || <span className="italic text-ink-faint">sans nom</span>}
            </span>
            {!bilingueOk && (
              <span title="Parité bilingue incomplète" className="text-amber text-[9px] font-bold">FR/EN</span>
            )}
          </div>
          {p.operator && (
            <div className="text-[11px] text-ink-muted truncate mt-0.5">{p.operator}</div>
          )}
          <div className="flex items-center gap-2 mt-1 text-[10.5px] text-ink-faint">
            {p.country && <span>{p.country}</span>}
            {p.instrumentType && <span className="before:content-['·'] before:mr-1.5">{p.instrumentType}</span>}
            {days !== null && (
              <span className="before:content-['·'] before:mr-1.5">
                vérif {relativeDate(p.lastVerifiedDate)}
              </span>
            )}
          </div>
        </div>
        <IconChevronRight className="w-3 h-3 text-ink-faint shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition" />
      </div>
    </button>
  );
}
